import cx from 'classnames';
import { map } from 'lodash';
import React, { Fragment, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { QrCode } from 'components/QrCode';
import { VtbProbeStatus } from 'components/VtbProbeStatus';
import { BANK_STYLES } from 'constants/bank-styles';
import { IMAGES_PATH } from 'constants/images.constants';
import { Bank, DeviceOS, EventName } from 'enums';
import { useDeepLink } from 'hooks/use-deeplink.hook';
import { useOrderContext } from 'hooks/use-order-context.hook';
import { useVtbTlsProbe } from 'hooks/use-vtb-tls-probe.hook';
import { TranslationNamespace } from 'i18n';
import { Deeplink } from 'types';
import { eventUtils, userDeviceUtils } from 'utils';

type Props = {
  deeplinks: Deeplink[];
  directOpen?: boolean;
};

export const DefaultDeeplinks: React.FC<Props> = ({
  deeplinks,
  directOpen,
}) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'components.payment_details',
  });

  const { getBankNameByCode } = useOrderContext();
  const { processDeeplinkUrl, hasMultipleUrls } = useDeepLink();
  const hasVtbDeeplink = useMemo(
    () => deeplinks.some((d) => d.customerBank === Bank.VTB),
    [deeplinks],
  );
  const { isReachable, isLoading: isVtbLoading } =
    useVtbTlsProbe(hasVtbDeeplink);
  const deviceOS = useMemo(() => userDeviceUtils.getDeviceOS(), []);

  return (
    <div className="tw-mb-4">
      {map(deeplinks, (deeplink) => {
        if (!deeplink.url) {
          return;
        }

        if (
          deeplink.customerBank === Bank.VTB &&
          (isVtbLoading || !isReachable)
        ) {
          return (
            <VtbProbeStatus
              key={deeplink.id}
              isLoading={isVtbLoading}
              fallbackText={t('deeplink.vtb_fallback')}
              className="tw-max-w-sm tw-m-auto"
            />
          );
        }

        const url = processDeeplinkUrl(deeplink.url);
        return (
          <div key={deeplink.id} className="tw-max-w-sm tw-m-auto">
            {deviceOS === DeviceOS.Desktop && !hasMultipleUrls(url) && (
              <Fragment>
                <QrCode
                  data={url}
                  title={t('qr_code.title', {
                    bankName:
                      getBankNameByCode?.(deeplink.customerBank) ||
                      deeplink.customerBank,
                  })}
                />
                <div className="tw-text-gray-400 tw-text-center tw-w-full tw-mb-4 tw-text-sm">
                  {t('qr_code_description')}
                </div>
              </Fragment>
            )}
            <DeepLinkButton
              href={url}
              bank={deeplink.customerBank as Bank}
              directOpen={directOpen}
            />
          </div>
        );
      })}
    </div>
  );
};

type DeepLinkButtonProps = {
  href: string;
  bank: Bank;
  directOpen?: boolean;
};

function DeepLinkButton({ href, bank, directOpen }: DeepLinkButtonProps) {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'components.payment_details',
  });
  const { getBankNameByCode } = useOrderContext();
  const { hasMultipleUrls, openDeepLinks } = useDeepLink();

  const [isLoading, setIsLoading] = useState(false);
  const [isFailed] = useState(false);

  const style = useMemo(() => BANK_STYLES[bank], [bank]);

  const bankName = useMemo(
    () => style?.name || getBankNameByCode?.(bank) || null,
    [style?.name, bank, getBankNameByCode],
  );

  const title = useMemo(
    () =>
      bankName
        ? t('deeplink.title', { bankName })
        : t('deeplink.other_bank', { defaultValue: 'Other bank' }),
    [bankName, t],
  );

  const content = useMemo(
    () =>
      style?.buttonIconPath ? (
        <img
          src={`${IMAGES_PATH}${style.buttonIconPath}`}
          alt={title}
          width={style.buttonIconWidth}
          height={style.buttonIconHeight}
        />
      ) : (
        <span>{title}</span>
      ),
    [style, title],
  );

  const onClick = useCallback(
    async (e: React.MouseEvent) => {
      eventUtils.trackEvent({
        event: EventName.DeeplinkOpen,
      });
      if (!directOpen && !hasMultipleUrls(href)) {
        return;
      }
      e.preventDefault();
      setIsLoading(true);
      await openDeepLinks(href);
      setIsLoading(false);
    },
    [href, openDeepLinks, hasMultipleUrls, directOpen],
  );

  const isDisabled = useMemo(
    () => isLoading || isFailed,
    [isLoading, isFailed],
  );

  const className = cx(
    `tw-w-full tw-h-[54px] tw-rounded-[8px] tw-text-white tw-font-medium tw-text-[17px] tw-flex tw-items-center tw-justify-center`,
    { 'tw-opacity-50 tw-cursor-wait': isLoading },
    {
      'tw-opacity-50 tw-cursor-not-allowed tw-pointer-events-none':
        isFailed || isDisabled,
    },
  );

  return (
    <a
      className={className}
      style={{ background: style?.gradient }}
      {...(directOpen ? {} : { target: '_blank', rel: 'noopener noreferrer' })}
      onClick={onClick}
      {...(directOpen || hasMultipleUrls(href) ? {} : { href })}
    >
      {content}
    </a>
  );
}
